import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import {
  LuRefrigerator,
  LuArrowLeft,
  LuUpload,
  LuLayoutGrid,
  LuZap,
  LuMonitorDot,
  LuScrollText,
  LuHistory,
  LuActivity,
  LuNetwork,
} from 'react-icons/lu';

import { RootState } from '@/store/root.reducer';
import {
  SMART_COOLER_PROPS,
  SMART_COOLER_STATUS_VALUES,
  SMART_COOLER_SYSTEM_STATE_VALUES,
  FetchLogRange,
  RANGE_DAYS_MAP,
  VIEW_SC_STRINGS,
} from '@/constant/smartCooler/smartCooler.constant';
import { APP_BG, APP_BORDER } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import { getRole, ROLES } from '@/constant/users/user.constant';

import AppButton from '@/components/atoms/AppButton';
import { Text } from '@/components/atoms/AppText';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { TabNav, TabNavOption } from '@/components/elements/TabNav';
import {
  triggerCoolerAction,
  resetCoolerAction,
} from '@/store/slices/smartCooler.slice';
import {
  fetchCoolerActionLogs,
  fetchCoolerConnectionLogs,
} from '@/services/smartCooler';
import {
  getAppLogs,
  getMoreAppLogs,
  downloadAppLog,
  resetAppLogs,
} from '@/store/slices/appLogs.slice';
import { getAppLogList } from '@/store/selectors/appLogs.selector';
import {
  IAppLog,
  ISmartCooler,
  IActionLog,
  IConnectionLog,
} from '@/pages/SmartCoolers/smartCooler.interface';
import FetchLogModal from './FetchLogModal';

// ── Sub-components ──────────────────────────────────────────────────────────
import { ViewSCKpiStrip } from './ViewSmartCooler/ViewSCKpiStrip';
import { ViewSCOverviewTab } from './ViewSmartCooler/ViewSCOverviewTab';
import { ViewSCSystemTab } from './ViewSmartCooler/ViewSCSystemTab';
import { ViewSCConnectionTab } from './ViewSmartCooler/ViewSCConnectionTab';
import { ViewSCActionLogsTab } from './ViewSmartCooler/ViewSCActionLogsTab';
import { ViewSCConnectionLogsTab } from './ViewSmartCooler/ViewSCConnectionLogsTab';
import { ViewSCDeviceLogsTab } from './ViewSmartCooler/ViewSCDeviceLogsTab';
import { useToast } from '@/components/organism/Toast/ToastProvider';

// ─── Tab definitions ─────────────────────────────────────────────────────────
const TABS: TabNavOption[] = [
  {
    value: 'overview',
    label: 'Overview',
    icon: <LuLayoutGrid className="w-3.5 h-3.5" />,
  },
  {
    value: 'system',
    label: 'System',
    icon: <LuMonitorDot className="w-3.5 h-3.5" />,
  },
  {
    value: 'connection',
    label: 'Connection',
    icon: <LuZap className="w-3.5 h-3.5" />,
  },
  {
    value: 'actionLogs',
    label: 'Action Logs',
    icon: <LuActivity className="w-3.5 h-3.5" />,
  },
  {
    value: 'connLogs',
    label: 'Connection Logs',
    icon: <LuNetwork className="w-3.5 h-3.5" />,
  },
  {
    value: 'logs',
    label: 'Device Logs',
    icon: <LuScrollText className="w-3.5 h-3.5" />,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Page — orchestrates state & renders sub-tab components
// ─────────────────────────────────────────────────────────────────────────────
const ViewSmartCooler = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storeItem = useSelector((s: RootState) =>
    s.smartCooler.data?.find((c) => c.coolerId === id),
  );
  const item: ISmartCooler | undefined = location.state?.item ?? storeItem;
  const {
    actionLoading,
    actionSuccess,
    actionError,
    actionMessage,
    actionData,
  } = useSelector((s: RootState) => s.smartCooler);

  const [activeTab, setActiveTab] = useState('overview');
  const [fetchLogModal, setFetchLogModal] = useState(false);

  const isSupportUser = getRole() === ROLES.SUPPORT;

  // ── Activity tab state ─────────────────────────────────────────────────
  const ACTIVITY_PAGE_SIZE = 5;
  const [actionLogsInitialized, setActionLogsInitialized] = useState(false);
  const [connLogsInitialized, setConnLogsInitialized] = useState(false);
  const [actionLogs, setActionLogs] = useState<IActionLog[]>([]);
  const [connLogs, setConnLogs] = useState<IConnectionLog[]>([]);
  const [actionLogsLoading, setActionLogsLoading] = useState(false);
  const [connLogsLoading, setConnLogsLoading] = useState(false);
  const [actionLogsLoadingMore, setActionLogsLoadingMore] = useState(false);
  const [connLogsLoadingMore, setConnLogsLoadingMore] = useState(false);
  const [actionLogsError, setActionLogsError] = useState<string | null>(null);
  const [connLogsError, setConnLogsError] = useState<string | null>(null);
  const [actionLogsTotalRecords, setActionLogsTotalRecords] = useState(0);
  const [connLogsTotalRecords, setConnLogsTotalRecords] = useState(0);
  const [actionLogsPage, setActionLogsPage] = useState(1);
  const [connLogsPage, setConnLogsPage] = useState(1);

  // ── Log tab state ──────────────────────────────────────────────────────────
  const APP_LOG_PAGE_SIZE = 5;
  const {
    appLogs: appLogData,
    loading: logsLoading,
    downloadInProgress,
    downloadError,
  } = useSelector(getAppLogList);
  const appLogs: IAppLog[] = appLogData?.data ?? [];
  const logsTotalRecords: number = appLogData?.pagination?.totalRecords ?? 0;
  const totalLogPages: number = appLogData?.pagination?.totalPages ?? 1;
  const [logsPage, setLogsPage] = useState(1);

  const [downloadingLogId, setDownloadingLogId] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const toast = useToast();

  const fetchDeviceLogs = useCallback(
    (page = 1) => {
      dispatch(resetAppLogs());
      dispatch(
        getAppLogs({
          page,
          limit: APP_LOG_PAGE_SIZE,
          orderBy: 'createdOn',
          order: 'desc',
          ...(item?.nativeId ? { androidDeviceId: item.nativeId } : {}),
          ...(item?.deviceId ? { deviceId: item.deviceId } : {}),
          ...(item?.marketId ? { marketId: item.marketId } : {}),
        }),
      );
      setLogsPage(page);
    },
    [dispatch, item?.nativeId, item?.deviceId, item?.marketId],
  );

  const handleLogPageChange = useCallback(
    (page: number) => {
      fetchDeviceLogs(page);
    },
    [fetchDeviceLogs],
  );

  useEffect(() => {
    if (activeTab === 'logs') fetchDeviceLogs(1);
  }, [activeTab, fetchDeviceLogs]);

  const fetchActionLogs = useCallback(
    (page = 1) => {
      if (!id) return;
      const isFirst = page === 1;
      if (isFirst) {
        setActionLogsLoading(true);
        setActionLogsError(null);
        setActionLogsPage(1);
      } else setActionLogsLoadingMore(true);
      fetchCoolerActionLogs(id, { page, limit: ACTIVITY_PAGE_SIZE })
        .then((res) => {
          const records: IActionLog[] = Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data)
              ? res.data
              : [];
          const total = res.data?.pagination?.totalRecords ?? records.length;
          setActionLogsTotalRecords(total);
          setActionLogsPage(page);
          const filtered = records.filter(
            (log) => !log.coolerId || log.coolerId === id,
          );
          setActionLogs((prev) =>
            isFirst ? filtered : [...prev, ...filtered],
          );
        })
        .catch(() => {
          if (isFirst) setActionLogsError('Failed to load action logs');
        })
        .finally(() => {
          if (isFirst) setActionLogsLoading(false);
          else setActionLogsLoadingMore(false);
        });
    },
    [id],
  );

  const loadMoreActionLogs = useCallback(() => {
    fetchActionLogs(actionLogsPage + 1);
  }, [fetchActionLogs, actionLogsPage]);

  const fetchConnLogs = useCallback(
    (page = 1) => {
      if (!id) return;
      const isFirst = page === 1;
      if (isFirst) {
        setConnLogsLoading(true);
        setConnLogsError(null);
        setConnLogsPage(1);
      } else setConnLogsLoadingMore(true);
      fetchCoolerConnectionLogs(id, { page, limit: ACTIVITY_PAGE_SIZE })
        .then((res) => {
          const records: IConnectionLog[] = Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data)
              ? res.data
              : [];
          const total = res.data?.pagination?.totalRecords ?? records.length;
          setConnLogsTotalRecords(total);
          setConnLogsPage(page);
          const filtered = records.filter(
            (log) => !log.coolerId || log.coolerId === id,
          );
          setConnLogs((prev) => (isFirst ? filtered : [...prev, ...filtered]));
        })
        .catch(() => {
          if (isFirst) setConnLogsError('Failed to load connection logs');
        })
        .finally(() => {
          if (isFirst) setConnLogsLoading(false);
          else setConnLogsLoadingMore(false);
        });
    },
    [id],
  );

  const loadMoreConnLogs = useCallback(() => {
    fetchConnLogs(connLogsPage + 1);
  }, [fetchConnLogs, connLogsPage]);

  // Clean up shared store on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAppLogs());
    };
  }, []);

  useEffect(() => {
    if (!downloadInProgress && downloadingLogId && !downloadError)
      setDownloadingLogId(null);
  }, [downloadInProgress, downloadingLogId, downloadError]);

  const handleLogDownload = (e: React.MouseEvent, logId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloadingLogId || downloadInProgress) return;
    setDownloadingLogId(logId);
    dispatch(downloadAppLog({ logId }));
  };

  // ── Not found ──────────────────────────────────────────────────────────────
  if (!item) {
    return (
      <div
        className={classNames(
          'flex h-full flex-col items-center justify-center gap-6 p-8',
          APP_BG.canvas,
        )}
      >
        <div
          className={classNames(
            'flex h-20 w-20 items-center justify-center rounded-3xl border-2',
            APP_BG.surface,
            APP_BORDER.soft,
          )}
        >
          <LuRefrigerator className="w-10 h-10 text-gray-300 dark:text-gray-600" />
        </div>
        <div className="text-center space-y-2">
          <Text as="p" size="xl" weight="bold" color="default">
            {VIEW_SC_STRINGS.NOT_FOUND_TITLE}
          </Text>
          <Text as="p" size="sm" color="faint">
            {VIEW_SC_STRINGS.NOT_FOUND_DESC}
          </Text>
        </div>
        <AppButton
          variant="neutral"
          size="md"
          icon={<LuArrowLeft className="w-4 h-4" />}
          onClick={() => navigate('/smartcoolers')}
        >
          {VIEW_SC_STRINGS.BACK_BUTTON}
        </AppButton>
      </div>
    );
  }

  const sysInfo = item.meta?.systemInfo ?? {};
  const hasSys = Object.keys(sysInfo).length > 0;
  const storageUsed =
    sysInfo.totalStorageMb && sysInfo.availableStorageMb
      ? Math.round(
        ((sysInfo.totalStorageMb - sysInfo.availableStorageMb) /
          sysInfo.totalStorageMb) *
        100,
      )
      : null;
  const isConnected = item.status === SMART_COOLER_STATUS_VALUES.CONNECTED;
  const isOnline = item.systemState === SMART_COOLER_SYSTEM_STATE_VALUES.ONLINE;

  const handleFetchSubmit = (it: ISmartCooler, range: FetchLogRange) => {
    dispatch(
      triggerCoolerAction({
        coolerId: it.coolerId,
        type: 'UPLOAD_LOGS',
        options: { days: RANGE_DAYS_MAP[range] },
      }),
    );
  };

    useEffect(() => {
      if (actionSuccess) {
        toast?.pushSuccess(
          actionMessage || VIEW_SC_STRINGS.COOLER_ACTION_SUCCESS_MSG,
        );
      }
      if (actionError) {
        toast?.pushError(actionError);
      }
    }, [actionSuccess, actionError]);

  return (
    <>
      <FullPageCard
        testId={tid(TID.VIEW_PAGE)}
        header={
          <PageToolbar
            size="md"
            showDivider={false}
            title={item.nativeId || item.coolerId}
            subtitle={`${SMART_COOLER_PROPS.HEADING} · ${item.coolerId}`}
            icon={<LuRefrigerator className="w-5 h-5" />}
            actionButtons={
              <div className="flex items-center gap-2">
                <AppButton
                  data-testid={tid(TID.VIEW_BACK_BTN)}
                  variant="neutral"
                  size="sm"
                  icon={<LuArrowLeft className="w-4 h-4" />}
                  onClick={() => navigate('/smartcoolers')}
                >
                  <span className="hidden sm:inline">
                    {VIEW_SC_STRINGS.BACK_SHORT}
                  </span>
                </AppButton>
                {!isSupportUser && (
                  <AppButton
                    data-testid={tid(TID.VIEW_FETCH_LOGS_BTN)}
                    variant="info"
                    size="sm"
                    disabled={!isConnected}
                    icon={<LuUpload className="w-3.5 h-3.5" />}
                    onClick={() => setFetchLogModal(true)}
                    title={
                      isConnected
                        ? SMART_COOLER_PROPS.TOOLTIPS.UPLOAD_LOGS
                        : SMART_COOLER_PROPS.TOOLTIPS.UPLOAD_UNAVAILABLE
                    }
                  >
                    <span className="hidden sm:inline">
                      {VIEW_SC_STRINGS.FETCH_LOGS_BUTTON}
                    </span>
                  </AppButton>
                )}
              </div>
            }
          />
        }
        pageClassName="!h-auto !overflow-visible"
        className="!overflow-visible"
        contentClassName="flex flex-col gap-0 !overflow-visible !min-h-0 !flex-none"
      >
        {/* ── KPI strip ─────────────────────────────────────────── */}
        <ViewSCKpiStrip
          item={item}
          isConnected={isConnected}
          isOnline={isOnline}
          storageUsed={storageUsed}
          sysInfo={sysInfo}
        />

        {/* ── Tab navigation ────────────────────────────────────── */}
        <div className={classNames('border-b', APP_BORDER.filterRow)}>
          <TabNav tabs={TABS} active={activeTab} onChange={setActiveTab} />
        </div>

        {/* ── Tab content ───────────────────────────────────────── */}
        <div
          className={classNames(
            'flex flex-col gap-5 p-4 sm:p-5',
            APP_BG.canvas,
          )}
        >
          {activeTab === 'overview' && <ViewSCOverviewTab item={item} />}

          {activeTab === 'system' && (
            <ViewSCSystemTab
              item={item}
              sysInfo={sysInfo}
              hasSys={hasSys}
              storageUsed={storageUsed}
            />
          )}

          {activeTab === 'connection' && (
            <ViewSCConnectionTab
              item={item}
              isConnected={isConnected}
              isOnline={isOnline}
              onOpenFetchModal={() => setFetchLogModal(true)}
              onSwitchToLogs={() => setActiveTab('logs')}
            />
          )}

          {activeTab === 'actionLogs' && (
            <ViewSCActionLogsTab
              coolerId={id}
              logs={actionLogs}
              loading={actionLogsLoading}
              loadingMore={actionLogsLoadingMore}
              error={actionLogsError}
              totalRecords={actionLogsTotalRecords}
              initialized={actionLogsInitialized}
              onInit={() => {
                setActionLogsInitialized(true);
                fetchActionLogs(1);
              }}
              onLoadMore={loadMoreActionLogs}
              onRefresh={() => {
                setActionLogs([]);
                fetchActionLogs(1);
              }}
            />
          )}

          {activeTab === 'connLogs' && (
            <ViewSCConnectionLogsTab
              coolerId={id}
              logs={connLogs}
              loading={connLogsLoading}
              loadingMore={connLogsLoadingMore}
              error={connLogsError}
              totalRecords={connLogsTotalRecords}
              initialized={connLogsInitialized}
              onInit={() => {
                setConnLogsInitialized(true);
                fetchConnLogs(1);
              }}
              onLoadMore={loadMoreConnLogs}
              onRefresh={() => {
                setConnLogs([]);
                fetchConnLogs(1);
              }}
            />
          )}

          {activeTab === 'logs' && (
            <ViewSCDeviceLogsTab
              item={item}
              logs={appLogs}
              logsLoading={logsLoading}
              logsTotalRecords={logsTotalRecords}
              logCurrentPage={logsPage}
              totalLogPages={totalLogPages}
              isConnected={isConnected}
              downloadInProgress={downloadInProgress}
              downloadingLogId={downloadingLogId}
              expandedLogId={expandedLogId}
              onRefresh={() => fetchDeviceLogs(1)}
              onPageChange={handleLogPageChange}
              onDownload={handleLogDownload}
              onExpandLog={setExpandedLogId}
              onOpenFetchModal={() => setFetchLogModal(true)}
            />
          )}
        </div>
      </FullPageCard>

      <FetchLogModal
        open={fetchLogModal}
        item={item}
        onClose={() => {
          dispatch(resetCoolerAction());
          setFetchLogModal(false);
        }}
        onSubmit={handleFetchSubmit}
        isLoading={actionLoading}
        isSuccess={actionSuccess}
        isError={!!actionError}
        actionMessage={actionMessage}
        actionData={actionData}
        actionError={actionError}
        onDoneClose={() => {
          dispatch(resetCoolerAction());
          setFetchLogModal(false);
        }}
      />
    </>
  );
};

export default ViewSmartCooler;
